import {MouseEventHandler, PropsWithChildren, RefCallback, type FC} from "react"
import {
  Stack,
  Typography,
  IconButton as IconButtonMUI,
  IconButtonProps,
  Grow,
} from "@mui/material"
import {NewButton} from "../../atoms"
import Joyride, {Step, StylesOptions} from "react-joyride"
import {
  ChevronCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "../../../assets"
import {StorageData} from "../../../hooks/useStorage"
import {useAppContext} from "../../../context/AppContext"
import {useTranslation} from "react-i18next"

export type GuidedTourBodyProps = {
  size: number
  continuous: boolean
  index: number
  step: Step
  backProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  closeProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  primaryProps: {
    "aria-label": string
    "data-action": string
    onClick: MouseEventHandler<HTMLElement>
    role: string
    title: string
  }
  tooltipProps: {
    "aria-modal": boolean
    ref: RefCallback<HTMLElement>
    role: string
  }
}

const IconButton: FC<PropsWithChildren<IconButtonProps>> = (props) => {
  const {children} = props
  return (
    <IconButtonMUI
      {...props}
      disableRipple={true}
      sx={{
        height: "40px",
        widht: "40px",
        color: props.disabled ? "#9b9b9b" : "#fff",
        borderRadius: "4px",
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: props.disabled ? "#eeeeee" : "#053747",
        borderColor: props.disabled ? "#bdbdbc" : "#053747",
        "&:hover": {
          backgroundColor: "#0A495D",
        },
      }}
    >
      {children}
    </IconButtonMUI>
  )
}

const GuidedTourBody = ({
  size,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
}: GuidedTourBodyProps) => {
  const {useGuidedTourStore} = useAppContext()
  const {t, i18n} = useTranslation()
  const setRun = useGuidedTourStore((state) => state.setRun)
  const setCompletedTour = useGuidedTourStore((state) => state.setCompletedTour)
  const getBorderDecoration = (step: Step) => {
    if (
      step.placement === "top" ||
      step.placement === "top-end" ||
      step.placement === "top-start"
    ) {
      return {
        borderTop: "unset",
        borderRight: "unset",
        borderBottom: "4px solid #22c3bb",
        borderLeft: "unset",
      }
    }

    if (
      step.placement === "right" ||
      step.placement === "right-end" ||
      step.placement === "right-start"
    ) {
      return {
        borderRight: "unset",
        borderTop: "unset",
        borderBottom: "unset",
        borderLeft: "4px solid #22c3bb",
      }
    }

    if (
      step.placement === "bottom" ||
      step.placement === "bottom-end" ||
      step.placement === "bottom-start"
    ) {
      return {
        borderBottom: "unset",
        borderRight: "unset",
        borderTop: "4px solid #22c3bb",
        borderLeft: "unset",
      }
    }

    if (
      step.placement === "left" ||
      step.placement === "left-end" ||
      step.placement === "left-start"
    ) {
      return {
        borderLeft: "unset",
        borderRight: "4px solid #22c3bb",
        borderBottom: "unset",
        borderTop: "unset",
      }
    }

    return {
      borderLeft: "unset",
      borderRight: "unset",
      borderBottom: "unset",
      borderTop: "unset",
    }
  }

  return (
    <Grow in>
      <Stack
        {...tooltipProps}
        gap="16px"
        sx={{
          backgroundColor: "white",
          padding: "16px",
          ...getBorderDecoration(step),
          borderRadius: "4px",
          maxWidth:
            i18n.languages[0] === "de" || i18n.languages[0] === "es"
              ? "425px"
              : i18n.languages[0] === "fr" || i18n.languages[0] === "pt_BR"
              ? "300px"
              : "268px",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize="16px" fontWeight={600} color="#242d35">
            {step.title}
          </Typography>
          {index + 1 < size && (
            <NewButton
              {...closeProps}
              onClick={() => {
                setRun(false)
                setCompletedTour(true)
                localStorage.setItem(StorageData.completedTour, "true")
              }}
              variant="outline"
              color="primary"
              size="small"
              text={t("Skip")}
              sx={{
                backgroundColor: "white",
                color: "#053747",
                textDecoration: "underline",
                textUnderlineOffset: "5px",
                border: "unset",
                "&:hover": {
                  textDecoration: "underline",
                  textDecorationThickness: "2px",
                  bgcolor: "transparent",
                  paddingTop: "2px",
                },
                "&:active": {
                  textDecorationThickness: "2px",
                  backgroundColor: "transparent",
                },
              }}
            />
          )}
        </Stack>
        <Typography fontSize="14px">{step.content}</Typography>
        <Stack>
          {index === 0 ? (
            <Stack
              justifyContent="space-between"
              alignItems="center"
              direction="row"
            >
              <IconButton disabled {...backProps}>
                <ChevronLeftIcon fill="#9b9b9b" />
              </IconButton>
              <Typography fontSize="12px" color="#595958">
                {t("Step out of", {index: index + 1, size})}
              </Typography>
              <IconButton {...primaryProps}>
                <ChevronRightIcon fill="#fff" />
              </IconButton>
            </Stack>
          ) : (
            <Stack
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              gap="16px"
            >
              <IconButton {...backProps}>
                <ChevronLeftIcon fill="#fff" />
              </IconButton>
              <Typography fontSize="12px" color="#595958">
                {t("Step out of", {index: index + 1, size})}
              </Typography>
              {index + 1 < size ? (
                <IconButton {...primaryProps}>
                  <ChevronRightIcon fill="#fff" />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    setRun(false)
                    setCompletedTour(true)
                    localStorage.setItem(StorageData.completedTour, "true")
                  }}
                >
                  <ChevronCheckIcon fill="#fff" />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Grow>
  )
}

export type GuidedTourProps = {
  run?: boolean
  setRun?: (run: boolean) => void
  steps: Step[]
  continuous?: boolean
  stylesOptions?: Partial<StylesOptions>
}

const GuidedTour: FC<GuidedTourProps> = (props) => {
  const {run, steps, continuous, stylesOptions} = props
  const {useGuidedTourStore} = useAppContext()
  const setRun = useGuidedTourStore((state) => state.setRun)
  const setCompletedTour = useGuidedTourStore((state) => state.setCompletedTour)

  return (
    <Joyride
      run={run}
      steps={[...steps]}
      continuous={continuous}
      callback={(data) => {
        if (
          data.action === "close" &&
          data.index + 1 === data.size &&
          data.lifecycle === "complete"
        ) {
          setRun(false)
          setCompletedTour(true)
          localStorage.setItem(StorageData.completedTour, "true")
        }
      }}
      tooltipComponent={GuidedTourBody}
      disableScrollParentFix={true}
      disableScrolling={true}
      floaterProps={{
        styles: {
          arrow: {
            length: 8,
            spread: 12,
            borderRadius: "50%",
          },
        },
      }}
      styles={{
        spotlight: {
          borderRadius: 0,
        },
        options: {
          arrowColor: "#22c3bb",
          zIndex: 9999,
          ...stylesOptions,
        },
      }}
    />
  )
}

export default GuidedTour
